import { useInfiniteScroll } from "ahooks"

type BaseQueryParams = {
    page: number
    page_size: number
}

interface PaginationQuery extends BaseQueryParams {}

interface PaginationResponse<T> {
    d?: T[]
    list?: T[]
    t?: number
    s?: number
}

interface UsePaginatedListOptions<Q, T> {
    fetcher: (query: PaginationQuery) => Promise<PaginationResponse<T> | undefined>
    initialQuery?: Q
    pageSize?: number
    reloadDeps?: (number | string | null | undefined)[]
}

/**
 * 通用分页 hook，基于 ahooks 的 useInfiniteScroll 实现
 *
 * @template Q 查询参数类型（如 { status: number }）
 * @template T 列表项类型（如 { id: number, name: string }）
 *
 * @param fetcher - 数据请求函数，返回符合 PaginationResponse 结构的结果
 * @param initialQuery - 初始请求参数
 * @param pageSize - 每页条数，默认为 15
 * @param reloadDeps - 请求依赖项变化时自动重载
 *
 * @returns {
 *   list: 当前分页数据累加后的列表
 *   loading: 是否正在加载（包括初始和加载更多）
 *   data: 原始返回数据
 *   noMore: 是否没有更多数据
 *   loadMore: 加载下一页函数
 *   reload: 重载函数（从第一页重新请求）
 * }
 */
export function usePaginatedList<Q extends object = {}, T extends object = {}>({
    fetcher,
    initialQuery,
    pageSize = 15,
    reloadDeps = [],
}: UsePaginatedListOptions<Q, T>) {
    const { loading, loadMore, noMore, reload, data, loadingMore } = useInfiniteScroll(
        async (data) => {
            const currentPage = Math.floor((data?.list?.length || 0) / pageSize) + 1
            const query: PaginationQuery = {
                ...(initialQuery ?? {}),
                page: currentPage,
                page_size: pageSize,
            }
            const res = await fetcher(query)
            const currentList = res?.d ?? []
            return {
                list: currentList,
                noMore: currentList.length < pageSize,
            }
        },
        {
            isNoMore: (d) => d?.noMore,
            reloadDeps,
        },
    )

    return {
        list: Array.isArray(data?.list) ? data.list : [],
        loading: loading || loadingMore,
        data,
        noMore,
        loadMore,
        reload,
    }
}
